import React, { useCallback, useMemo, useState } from 'react';
import { Text, View } from 'react-native';
import InventoryPanel from './InventoryPanel';
import InventoryCategoryPicker from './InventoryCategoryPicker';
import SheetHeader from '../../../components/Reusables/bottomSheet/SheetHeader';
import {
  InventoryButton,
  InventoryChip,
  InventoryTextLink,
} from './InventoryParts';
import type { InventoryStyles } from './inventoryStyles';
import type { InventoryCategory } from './useInventoryCategories';
import { useFilterSheetCounts, type DraftCounts } from './useFilterSheetCounts';
import {
  DEFAULT_INVENTORY_FILTERS,
  SHOW_ONLY_OPTIONS,
  STOCK_STATUS_OPTIONS,
  areFiltersEqual,
  getCategoryLabel,
  toggleCategory,
  toggleShowOnly,
  type InventoryFilters,
} from '../../../utils/inventoryFilters';
import type { CategorySelection } from '../../../utils/inventoryCategorySelection';

export type { DraftCounts };

type Props = {
  styles: InventoryStyles;
  theme: any;
  open: boolean;
  filters: InventoryFilters;
  /** The list's total for the filters as applied, for the button while counts load. */
  total: number | null;
  categories: InventoryCategory[] | null;
  /** The categories request failed; `categories` is then null. */
  categoriesFailed: boolean;
  onRetryCategories: () => void;
  /** Counts for a draft (a flagged limit=1 request); null when it fails. */
  fetchDraftCounts: (draft: InventoryFilters) => Promise<DraftCounts | null>;
  onApply: (filters: InventoryFilters) => void;
  onClose: () => void;
};

const TOP_CATEGORY_CHIPS = 7;

/**
 * The Filters sheet: stock status, category and "show only", edited as a
 * draft and applied with "Show N products". Counts follow the draft.
 */
const InventoryFiltersSheet: React.FC<Props> = ({
  styles,
  theme,
  open,
  filters,
  total,
  categories,
  categoriesFailed,
  onRetryCategories,
  fetchDraftCounts,
  onApply,
  onClose,
}) => {
  const [draft, setDraft] = useState<InventoryFilters>(filters);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Start each opening from the applied filters, during render, so the first
  // counts request is for them and not for an edit left from last time.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(filters);
  }

  const draftCounts = useFilterSheetCounts(open, draft, fetchDraftCounts);
  const isDraftApplied = areFiltersEqual(draft, filters);
  const shownCounts = draftCounts?.counts ?? null;
  const shownTotal = draftCounts?.total ?? (isDraftApplied ? total : null);

  const categoryChips = useMemo(() => {
    const nodes = categories ?? [];
    const top = [...nodes]
      .filter(node => node.category)
      .sort((a, b) => b.products - a.products)
      .slice(0, TOP_CATEGORY_CHIPS)
      .map(node => node.category);
    const selected = draft.categories.filter(key => !top.includes(key));
    return [...top, ...selected];
  }, [categories, draft.categories]);

  const totalCategories = useMemo(
    () => (categories ?? []).filter(node => node.category).length,
    [categories],
  );

  const onPickerDone = useCallback((selection: CategorySelection) => {
    setDraft(current => ({ ...current, ...selection }));
    setPickerOpen(false);
  }, []);

  const showLabel =
    shownTotal == null
      ? 'Show products'
      : `Show ${shownTotal} ${shownTotal === 1 ? 'product' : 'products'}`;

  return (
    <>
      <InventoryPanel
        styles={styles}
        open={open}
        onClose={onClose}
        snapPoints={['88%']}
        header={
          <SheetHeader title="Filters" onClose={onClose} />
        }
        footer={
          <>
            <InventoryButton
              styles={styles}
              theme={theme}
              label="Clear filters"
              variant="ghost"
              onPress={() => setDraft(DEFAULT_INVENTORY_FILTERS)}
            />
            {/* Blue only once there is a change to apply. */}
            <InventoryButton
              styles={styles}
              theme={theme}
              label={showLabel}
              variant={isDraftApplied ? 'ghost' : 'primary'}
              trailingIcon="chevron-right"
              onPress={() => {
                onApply(draft);
                onClose();
              }}
              style={styles.sheetFooterPrimary}
            />
          </>
        }>
        <View style={styles.sheetBody}>
          <View style={styles.section}>
            <View style={styles.sectionHead}>
              <Text style={styles.sectionTitle}>Stock status</Text>
            </View>
            <View style={styles.chipWrap} accessibilityRole="radiogroup">
              {STOCK_STATUS_OPTIONS.map(option => (
                <InventoryChip
                  key={option.key}
                  styles={styles}
                  theme={theme}
                  label={option.label}
                  count={shownCounts ? shownCounts[option.key] : '—'}
                  selected={draft.stockStatus === option.key}
                  onPress={() =>
                    setDraft(current => ({
                      ...current,
                      stockStatus: option.key,
                    }))
                  }
                />
              ))}
            </View>
          </View>

          <View style={styles.section}>
            <View style={styles.sectionHead}>
              <Text style={styles.sectionTitle}>Category</Text>
              {categories ? (
                <InventoryTextLink
                  styles={styles}
                  theme={theme}
                  label={
                    totalCategories ? `See all ${totalCategories}` : 'See all'
                  }
                  chevron
                  onPress={() => setPickerOpen(true)}
                />
              ) : categoriesFailed ? (
                <InventoryTextLink
                  styles={styles}
                  theme={theme}
                  label="Try again"
                  onPress={onRetryCategories}
                />
              ) : null}
            </View>
            {categories == null ? (
              <Text style={styles.sheetHeaderNote}>
                {categoriesFailed
                  ? 'Couldn’t load categories'
                  : 'Loading categories…'}
              </Text>
            ) : categoryChips.length === 0 &&
              draft.subCategories.length === 0 ? (
              <Text style={styles.sheetHeaderNote}>No categories yet</Text>
            ) : (
              <View style={styles.chipWrap}>
                {categoryChips.map(key => (
                  <InventoryChip
                    key={key}
                    styles={styles}
                    theme={theme}
                    label={getCategoryLabel(key)}
                    selected={draft.categories.includes(key)}
                    onPress={() =>
                      setDraft(current => toggleCategory(current, key))
                    }
                  />
                ))}
                {draft.subCategories.map(sub => (
                  <InventoryChip
                    key={`sub:${sub}`}
                    styles={styles}
                    theme={theme}
                    label={sub}
                    selected
                    onPress={() =>
                      setDraft(current => ({
                        ...current,
                        subCategories: current.subCategories.filter(
                          item => item !== sub,
                        ),
                      }))
                    }
                  />
                ))}
              </View>
            )}
          </View>

          <View style={styles.section}>
            <View style={styles.sectionHead}>
              <Text style={styles.sectionTitle}>Show only</Text>
            </View>
            <View style={styles.chipWrap}>
              {SHOW_ONLY_OPTIONS.map(option => (
                <InventoryChip
                  key={option.key}
                  styles={styles}
                  theme={theme}
                  label={option.label}
                  selected={draft.showOnly.includes(option.key)}
                  onPress={() =>
                    setDraft(current => toggleShowOnly(current, option.key))
                  }
                />
              ))}
            </View>
          </View>
        </View>
      </InventoryPanel>

      <InventoryCategoryPicker
        styles={styles}
        theme={theme}
        open={pickerOpen}
        categories={categories ?? []}
        selection={{
          categories: draft.categories,
          subCategories: draft.subCategories,
        }}
        onDone={onPickerDone}
        onClose={() => setPickerOpen(false)}
      />
    </>
  );
};

export default InventoryFiltersSheet;
